import React from 'react';
import { motion } from 'framer-motion';
import { Edit2, Trash2, ExternalLink, MapPin, Target, Users } from 'lucide-react';
import Images from '../../utils/Images';

const ChannelCard = ({ channel, channelType, index, onEdit, onDelete, icon: Icon }) => {
  const getDisplayLink = (link) => {
    if (!link) return '';
    // Truncate long URLs for display
    if (link.length > 35) {
      return link.substring(0, 35) + '...';
    }
    return link;
  };

  const getStoreInfo = (store) => {
    const stores = {
      play_store: { icon: Images.play_store, label: 'Play Store' },
      apple_store: { icon: Images.apple_store, label: 'App Store' },
      huawei: { icon: Images.huawei, label: 'Huawei Store' }
    };
    return stores[store] || null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center space-x-2">
          <Icon className="w-5 h-5 text-gray-600" />
          <h4 className="text-sm font-semibold text-gray-800">
            {channelType.charAt(0).toUpperCase() + channelType.slice(1)} #{index + 1}
          </h4>
        </div>
        <div className="flex items-center space-x-2">
          <motion.button
            onClick={() => onEdit(index)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-1.5 text-blue-600 rounded-md transition-colors hover:bg-blue-50"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </motion.button>
          <motion.button
            onClick={() => onDelete(index)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-1.5 text-red-600 rounded-md transition-colors hover:bg-red-50"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </motion.button>
        </div>
      </div>

      <div className="space-y-2">
        {/* Channel Link/Handle - For non-mobile apps */}
        {channelType !== 'mobile_app' && (
          <div className="flex items-start space-x-2">
            <ExternalLink className="flex-shrink-0 w-4 h-4 mt-0.5 text-gray-400" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-500">Handle/Link</p>
              <p className="text-sm text-gray-900 truncate" title={channel.handle || channel.link}>
                {getDisplayLink(channel.handle || channel.link)}
              </p>
            </div>
          </div>
        )}

        {/* App Stores - For Mobile App (Multiple Stores) */}
        {channelType === 'mobile_app' && channel.stores && Object.keys(channel.stores).length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500 mb-2">App Store Links</p>
            {Object.entries(channel.stores).map(([storeKey, storeLink]) => {
              const storeInfo = getStoreInfo(storeKey);
              if (!storeInfo || !storeLink) return null;
              
              return (
                <div key={storeKey} className="flex items-start space-x-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                  <img 
                    src={storeInfo.icon} 
                    alt={storeInfo.label}
                    className="w-6 h-6 object-contain flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-700">{storeInfo.label}</p>
                    <a
                      href={storeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 truncate block"
                      title={storeLink}
                    >
                      {getDisplayLink(storeLink)}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Audience Size */}
        <div className="flex items-start space-x-2">
          <Users className="flex-shrink-0 w-4 h-4 mt-0.5 text-gray-400" />
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-500">Audience Size</p>
            <p className="text-sm text-gray-900">{channel.audienceSize}</p>
          </div>
        </div>

        {/* Location & Purpose */}
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-start space-x-2">
            <MapPin className="flex-shrink-0 w-4 h-4 mt-0.5 text-gray-400" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-500">Location</p>
              <p className="text-sm text-gray-900 truncate" title={channel.location}>
                {channel.location}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <Target className="flex-shrink-0 w-4 h-4 mt-0.5 text-gray-400" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-500">Purpose</p>
              <p className="text-sm text-gray-900 truncate" title={channel.purpose}>
                {channel.purpose}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ChannelCard;
